import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInAnonymously,
  signInWithPopup,
  signOut as firebaseSignOut,
  User as FirebaseUser,
} from 'firebase/auth';
import { schoolAuth } from '../utils/schoolFirebase';
import { getStudentSession, getTeacher } from '../utils/schoolFirestore';
import { linkStudentSession } from '../utils/schoolApi';
import type { SchoolSession } from '../types';

interface SchoolAuthContextValue {
  session: SchoolSession | null;
  loading: boolean;
  signInTeacher: () => Promise<void>;
  signInStudent: (classCode: string, studentCode: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const SchoolAuthContext = createContext<SchoolAuthContextValue | null>(null);

/**
 * Role comes from Firestore, not token claims: students from schoolSessions/{uid}
 * (anonymous auth), teachers from schoolTeachers/{email} (Google auth).
 * Returns null for an anonymous user with no session (e.g. a failed or stale login).
 */
async function resolveSession(user: FirebaseUser): Promise<SchoolSession | null> {
  if (user.isAnonymous) {
    const studentSession = await getStudentSession(user.uid);
    return studentSession ? { role: 'student', uid: user.uid, ...studentSession } : null;
  }
  const email = user.email?.toLowerCase() ?? null;
  if (email) {
    const teacher = await getTeacher(email);
    if (teacher) return { role: 'teacher', uid: user.uid, email, teacher };
  }
  return { role: 'unauthorized', uid: user.uid, email };
}

interface SchoolAuthProviderProps {
  children: React.ReactNode;
}

export const SchoolAuthProvider: React.FC<SchoolAuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<SchoolSession | null>(null);
  const [loading, setLoading] = useState(true);
  // While a student login is in flight the anonymous user exists before its
  // schoolSessions doc does; skip resolving until signInStudent finishes.
  const studentLoginPending = useRef(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(schoolAuth, async (user) => {
      if (!user) {
        setSession(null);
        setLoading(false);
        return;
      }
      if (studentLoginPending.current) return;
      setLoading(true);
      try {
        setSession(await resolveSession(user));
      } catch (err) {
        console.error('[school] Failed to resolve session', err);
        setSession({ role: 'unauthorized', uid: user.uid, email: user.email ?? null });
      } finally {
        setLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  const signInTeacher = useCallback(async (): Promise<void> => {
    await signInWithPopup(schoolAuth, new GoogleAuthProvider());
  }, []);

  const signInStudent = useCallback(
    async (classCode: string, studentCode: string): Promise<void> => {
      studentLoginPending.current = true;
      try {
        const user = schoolAuth.currentUser?.isAnonymous
          ? schoolAuth.currentUser
          : (await signInAnonymously(schoolAuth)).user;
        await linkStudentSession(classCode, studentCode);
        setSession(await resolveSession(user));
      } catch (err) {
        // Don't leave a dangling anonymous user signed in after a failed login.
        await firebaseSignOut(schoolAuth).catch((e: unknown) =>
          console.error('[school] signOut after failed login', e),
        );
        throw err;
      } finally {
        studentLoginPending.current = false;
      }
    },
    [],
  );

  const signOut = useCallback(async (): Promise<void> => {
    await firebaseSignOut(schoolAuth);
  }, []);

  return (
    <SchoolAuthContext.Provider value={{ session, loading, signInTeacher, signInStudent, signOut }}>
      {children}
    </SchoolAuthContext.Provider>
  );
};

export function useSchoolAuth(): SchoolAuthContextValue {
  const ctx = useContext(SchoolAuthContext);
  if (!ctx) throw new Error('useSchoolAuth must be used within SchoolAuthProvider');
  return ctx;
}

export default SchoolAuthProvider;
